/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectVisualFilters,
  selectHotelName,
  selectTargetDate,
  selectPmsSync,
  selectEnableHotelGroups,
  selectReportType,
  selectLocalCurrency,
  selectInvalidPrimaryHotelList,
  selectIsPrimaryHotelSetOverTime,
  selectBreakdownDetails,
} from 'modules/dashboard/selectors';
import {
  generateRegionalMapShareUrl,
  getBookingDateSchema,
  getHotelIdSchema,
  getPmsSyncToggleSchema,
  getTargetDateSchema,
  getChartBreakdownFilterList,
  getSegmentFocusOnSchema,
  getDashboardPmsSyncSchema,
  getFocusOnFilter,
} from 'modules/dashboard/components/tab-container/inner-filter/functions';
import {
  selectRegionMapBookingDate,
  selectRegionMapFocusOn,
  selectRegionMapChartBreakdown,
  selectIsRegionDetailed,
  selectRegionalMapOtb,
  selectRegionalBreakdownTabularView,
  selectRegionMapFilterList,
  selectIsFocusOnSet,
  selectRegionIsDisplay,
  selectRegionTabularIsDisplay,
} from 'modules/dashboard/components/tab-container/regional-map-tab/selectors';
import { regionalMapActions } from 'modules/dashboard/components/tab-container/regional-map-tab/slice';
import { getSelectedBreakdown } from 'modules/dashboard/functions';
import writeLog from 'modules/common/utils/filter-log';
import { FILTER_VALUES, REPORT_TYPE } from 'modules/dashboard/constants';
/**
 * Custom hook to generate region map filter schemas
 */
const useRegionMap = () => {
  const dispatch = useDispatch();
  //
  const bookingDate = useSelector(selectRegionMapBookingDate);
  const targetDate = useSelector(selectTargetDate);
  const hotelId = useSelector(selectHotelName);
  const visualFilters = useSelector(selectVisualFilters);
  const pmsSyncToggle = useSelector(selectPmsSync);
  const focusOn = useSelector(selectRegionMapFocusOn);
  const chartBreakdown = useSelector(selectRegionMapChartBreakdown);
  const isDetailed = useSelector(selectIsRegionDetailed);
  const customBooking = useSelector(selectRegionalMapOtb);
  const regionalBreakdownTabularView = useSelector(selectRegionalBreakdownTabularView);
  const enableHotelGroups = useSelector(selectEnableHotelGroups);
  const reportType = useSelector(selectReportType);
  const filterList = useSelector(selectRegionMapFilterList);
  const localCurrency = useSelector(selectLocalCurrency);
  const IsFocusOnSet = useSelector(selectIsFocusOnSet);
  const isPrimaryHotelSetOverTime = useSelector(selectIsPrimaryHotelSetOverTime);
  const invalidPrimaryHotels = useSelector(selectInvalidPrimaryHotelList);
  const regionIsDisplay = useSelector(selectRegionIsDisplay);
  const regionTabularIsDisplay = useSelector(selectRegionTabularIsDisplay);
  const breakdownData = useSelector(selectBreakdownDetails);
  // focus on option list of selected breakdown
  const focusOnList = useMemo(() => {
    const selectedBreakdown = getSelectedBreakdown(visualFilters, chartBreakdown, isDetailed);
    const breakdown =
      selectedBreakdown === FILTER_VALUES.RATE_CATEGORY ||
      selectedBreakdown === FILTER_VALUES.RATE_CATEGORY_RAW
        ? FILTER_VALUES.RATE_CATEGORY_RAW
        : selectedBreakdown;
    return getFocusOnFilter(breakdownData, breakdown);
  }, [breakdownData, visualFilters, chartBreakdown, isDetailed]);
  // Generate filter list for regional map visual
  useEffect(() => {
    if (
      !IsFocusOnSet ||
      reportType !== REPORT_TYPE.REGION_MAP ||
      (!chartBreakdown && !targetDate && !hotelId && !bookingDate)
    ) {
      return;
    }
    const bookingDateFilter = getBookingDateSchema(
      visualFilters,
      bookingDate?.startDate,
      bookingDate?.endDate
    );
    const targetDateFilter = getTargetDateSchema(
      visualFilters,
      targetDate?.startDate,
      targetDate?.endDate
    );
    // const hotelIdFilter = getHotelIdSchema(hotelId?.hotelCode, visualFilters);
    // Extract invalid primary IDs
    const invalidPrimaryIds = invalidPrimaryHotels.map((hotel) => hotel.hotelCode);
    //
    let hotelIdFilter = [];
    if (enableHotelGroups) {
      let hotelListIds;
      if (isPrimaryHotelSetOverTime) {
        hotelListIds = hotelId.hotels
          .filter((hotel) => !invalidPrimaryIds.includes(hotel?.hotelCode))
          .map((hotel) => hotel?.hotelCode);
      } else {
        hotelListIds = hotelId?.hotels?.map((hotel) => hotel?.hotelCode) ?? [];
      }
      hotelIdFilter = getHotelIdSchema(hotelListIds, visualFilters);
    } else {
      hotelIdFilter = getHotelIdSchema(hotelId?.hotelCode, visualFilters);
    }
    const pmsSyncFilter = getPmsSyncToggleSchema(visualFilters, pmsSyncToggle);
    const chartBreakdownFilter = getChartBreakdownFilterList(
      visualFilters,
      chartBreakdown,
      isDetailed
    );
    const selectedBreakdown = getSelectedBreakdown(visualFilters, chartBreakdown, isDetailed);
    const segmentFocusOnFilter = getSegmentFocusOnSchema(
      selectedBreakdown,
      focusOn?.map((item) => item?.label)
    );
    const pmsSyncSchema = getDashboardPmsSyncSchema(pmsSyncToggle);
    //
    if (
      bookingDateFilter &&
      targetDateFilter &&
      hotelIdFilter &&
      chartBreakdownFilter &&
      segmentFocusOnFilter
    ) {
      const filters = [
        ...pmsSyncFilter,
        bookingDateFilter,
        targetDateFilter,
        hotelIdFilter,
        chartBreakdownFilter,
        ...segmentFocusOnFilter,
        ...pmsSyncSchema,
      ];

      if (JSON.stringify(filters) !== JSON.stringify(filterList)) {
        //
        writeLog('Regional Map Filter', filters);
        dispatch(regionalMapActions.setRegionMapFilterList(filters));
      }
    }
  }, [
    focusOn,
    chartBreakdown,
    bookingDate,
    visualFilters,
    targetDate,
    hotelId,
    pmsSyncToggle,
    isDetailed,
    enableHotelGroups,
    reportType,
    IsFocusOnSet,
    isPrimaryHotelSetOverTime,
    invalidPrimaryHotels,
  ]);
  // Triggers when filter values dependencies changes
  useEffect(() => {
    if (!focusOn && !chartBreakdown && !bookingDate && !targetDate && !hotelId) return;
    // Generate regional map filter url
    const regionalMapUrl = generateRegionalMapShareUrl(
      pmsSyncToggle,
      hotelId,
      targetDate,
      customBooking,
      visualFilters,
      chartBreakdown,
      isDetailed,
      bookingDate,
      focusOn,
      regionalBreakdownTabularView,
      hotelId,
      enableHotelGroups,
      localCurrency,
      regionIsDisplay,
      regionTabularIsDisplay,
      focusOnList
    );
    //
    dispatch(regionalMapActions.setRegionalMapUrl(regionalMapUrl));
  }, [
    pmsSyncToggle,
    chartBreakdown,
    bookingDate,
    visualFilters,
    customBooking,
    targetDate,
    hotelId,
    isDetailed,
    focusOn,
    regionalBreakdownTabularView,
    hotelId,
    enableHotelGroups,
    localCurrency,
    regionIsDisplay,
    regionTabularIsDisplay,
    focusOnList,
  ]);
};
//
export default useRegionMap;
